import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  ExternalLink,
  Filter,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { notificationsApi, type NotificationDto } from '@/api/notifications.api';

// ── helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── type badge colours ────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  WorkflowSubmitted:   'bg-blue-100 text-blue-700',
  WorkflowApproved:    'bg-green-100 text-green-700',
  WorkflowRejected:    'bg-red-100 text-red-700',
  WorkflowEscalated:   'bg-orange-100 text-orange-700',
  SchedulerFailure:    'bg-red-100 text-red-700',
  PostingFailure:      'bg-red-100 text-red-700',
  SystemAlert:         'bg-yellow-100 text-yellow-700',
  RecurringJournalDue: 'bg-indigo-100 text-indigo-700',
  AccrualDue:          'bg-purple-100 text-purple-700',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [isFetching,    setIsFetching]    = useState(false);
  const [unreadOnly,    setUnreadOnly]    = useState(false);

  // ── fetch ────────────────────────────────────────────────────────────────
  const load = useCallback(async (unread: boolean) => {
    setIsFetching(true);
    try {
      const items = await notificationsApi.getMyNotifications(unread, 100);
      setNotifications(items);
    } catch {
      setNotifications([]);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, []);

  useEffect(() => { load(unreadOnly); }, [load, unreadOnly]);

  // ── actions ──────────────────────────────────────────────────────────────
  const handleRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
    } catch { /* silent */ }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch { /* silent */ }
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CheckCheck size={15} />
              Mark all read
            </button>
          )}
          <button
            onClick={() => load(unreadOnly)}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter size={14} className="text-gray-400" />
        <button
          onClick={() => setUnreadOnly(false)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            !unreadOnly
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setUnreadOnly(true)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            unreadOnly
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          Unread
        </button>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-16 text-center">
            <Bell size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {unreadOnly ? 'No unread notifications' : 'No notifications yet'}
            </p>
            {unreadOnly && (
              <button
                onClick={() => setUnreadOnly(false)}
                className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Show all notifications
              </button>
            )}
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              className={`flex items-start gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                !n.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
              }`}
              onClick={() => {
                if (!n.isRead) handleRead(n.id);
                if (n.actionUrl) navigate(n.actionUrl);
              }}
            >
              {/* Unread dot */}
              <div className="pt-1.5 flex-shrink-0">
                {!n.isRead ? (
                  <span className="w-2 h-2 rounded-full bg-blue-500 block" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-transparent block" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-medium ${n.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'}`}>
                    {n.title}
                  </p>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                    {timeAgo(n.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                  {n.message}
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  {n.typeName && (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${TYPE_COLORS[n.typeName] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {n.typeName.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  )}
                  {n.module && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {n.module}
                    </span>
                  )}
                  {n.actionUrl && (
                    <ExternalLink size={11} className="text-gray-400 dark:text-gray-500" />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
